import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import moment from 'moment';
import {DownloadService} from '../../_zen-legacy-common/zen-common-services/_services/download.service';
import {
  BillCreateRequestFor,
  BillDto,
  BillDtoFor,
  BillOverlapResponse,
  BillUpdateRequestFor,
  PhysicalBillType
} from '../../_model/bills-v4.model';
import {CommodityType} from '../../_zen-legacy-common/_models/commodity';
import {PageableModel} from '../../../_modules/portfolio/_model/pageable.model';
import {sanitizeFilename} from '../../_utils/zen-filename.util';
import {BillsV4ElecService} from './bills-v4-elec.service';
import {BillsV4GasService} from './bills-v4-gas.service';
import {BudgetBillDialogRequest} from '../../../_modules/budget/_model/budget-bill-dialog.model';

/** Routes per-commodity bill operations to BillsV4ElecService or BillsV4GasService based on commodityType. */
@Injectable({providedIn: 'root'})
export class BillsV4Service {
  constructor(
    private billsV4ElecService: BillsV4ElecService,
    private billsV4GasService: BillsV4GasService,
    private downloadService: DownloadService,
  ) {}

  private pick(commodity: CommodityType) {
    if (commodity === CommodityType.Gas) return this.billsV4GasService;
    if (commodity === CommodityType.Electricity) return this.billsV4ElecService;
    throw new Error(`BillsV4Service: unsupported commodity '${commodity}' — expected Electricity or Gas.`);
  }

  getBillsByMeterId<C extends CommodityType>(commodity: C, customerId: number, serviceAddressId: number, meterId: number,
                                             size: number, sort?: string): Observable<PageableModel<BillDtoFor<C>>> {
    return this.pick(commodity).getBillsByMeterId(customerId, serviceAddressId, meterId, size, sort) as
      Observable<PageableModel<BillDtoFor<C>>>;
  }

  createBills<C extends CommodityType>(commodity: C, customerId: number, serviceAddressId: number, meterId: number,
                                       request: BillCreateRequestFor<C>): Observable<BillDtoFor<C>> {
    return this.pick(commodity).createBills(customerId, serviceAddressId, meterId, request as never) as
      Observable<BillDtoFor<C>>;
  }

  updateBills<C extends CommodityType>(commodity: C, customerId: number, serviceAddressId: number, meterId: number,
                                       billId: number,
                                       body: Partial<Omit<BillUpdateRequestFor<C>, 'id'>>): Observable<BillDtoFor<C>> {
    return this.pick(commodity).updateBills(customerId, serviceAddressId, meterId, billId, body as never) as
      Observable<BillDtoFor<C>>;
  }

  updateBillFile<C extends CommodityType>(commodity: C, customerId: number, serviceAddressId: number, meterId: number,
                                          billId: number, tempFileId: string,
                                          physicalBillType?: PhysicalBillType): Observable<BillDtoFor<C>> {
    return this.pick(commodity).updateBillFile(customerId, serviceAddressId, meterId, billId, tempFileId, physicalBillType) as
      Observable<BillDtoFor<C>>;
  }

  getBillDetailsById<C extends CommodityType>(commodity: C, customerId: number, serviceAddressId: number, meterId: number,
                                              billId: number): Observable<BillDtoFor<C>> {
    return this.pick(commodity).getBillDetailsById(customerId, serviceAddressId, meterId, billId) as
      Observable<BillDtoFor<C>>;
  }

  deleteBills(commodity: CommodityType, customerId: number, serviceAddressId: number, meterId: number,
              billId: number): Observable<void> {
    return this.pick(commodity).deleteBills(customerId, serviceAddressId, meterId, billId);
  }

  bulkDeleteBills(commodity: CommodityType, customerId: number, serviceAddressId: number, meterId: number,
                  billIds: number[]): Observable<void> {
    return this.pick(commodity).bulkDeleteBills(customerId, serviceAddressId, meterId, billIds);
  }

  validateBillDates(commodity: CommodityType, customerId: number, serviceAddressId: number, meterId: number,
                    billStartDate: moment.Moment, billEndDate: moment.Moment,
                    billId?: number): Observable<BillOverlapResponse> {
    return this.pick(commodity).validateBillDates(customerId, serviceAddressId, meterId, billStartDate, billEndDate, billId);
  }

  downloadBill(commodity: CommodityType, customerId: number, serviceAddressId: number, meterId: number,
               billId: number, fileName?: string): Observable<void> {
    return this.pick(commodity).downloadBill(customerId, serviceAddressId, meterId, billId, fileName);
  }

  /** Lives on the dispatcher to avoid duplicating filename construction across the per-commodity services. */
  downloadBillByType(commodity: CommodityType, request: BudgetBillDialogRequest, editBillData: BillDto,
                     physicalBillType: PhysicalBillType): void {
    const url = this.pick(commodity).downloadBillByTypeUrl(
      request.customerId, request.serviceAddressId, request.meter.meterId, request.bill.billId, physicalBillType,
    );
    const startStr = moment(editBillData.generalInformation?.billStartDate).format('MMM D, YYYY');
    const endStr = moment(editBillData.generalInformation?.billEndDate).format('MMM D, YYYY');
    const fileName = `${startStr}__${endStr}__${request.meter.label || request.meter.uan1}_${physicalBillType}`;
    this.downloadService.downloadApi(url, sanitizeFilename(fileName) + '.pdf', 'application/pdf');
  }

  deleteBillFile(commodity: CommodityType, customerId: number, serviceAddressId: number, meterId: number,
                 billId: number, physicalBillType: PhysicalBillType): Observable<void> {
    return this.pick(commodity).deleteBillFile(customerId, serviceAddressId, meterId, billId, physicalBillType);
  }

  /** Download one of a bill's physical files (utility or supplier slot) with a caller-built file name. */
  downloadBillFileByType(commodity: CommodityType, customerId: number, serviceAddressId: number, meterId: number,
                         billId: number, physicalBillType: PhysicalBillType, fileName: string): Promise<void> {
    const url = this.pick(commodity).downloadBillByTypeUrl(customerId, serviceAddressId, meterId, billId, physicalBillType);
    return this.downloadService.downloadApi(url, sanitizeFilename(fileName) + '.pdf', 'application/pdf');
  }

  /** Open one of a bill's physical files (utility or supplier slot) in a new browser tab. */
  previewBillFileByType(commodity: CommodityType, customerId: number, serviceAddressId: number, meterId: number,
                        billId: number, physicalBillType: PhysicalBillType): Promise<void> {
    const url = this.pick(commodity).downloadBillByTypeUrl(customerId, serviceAddressId, meterId, billId, physicalBillType);
    return this.downloadService.openApiFileInNewTab(url);
  }
}
